/**
 * Turning a line you own into a line on your ship.
 *
 * You draw freely between grid points, so the length you want is almost never a
 * length you happen to have — at arbitrary angles it is usually irrational, and
 * demanding an exact match would make the editor unusable.
 *
 * Cutting is what resolves that. Any station can cut a line down, so what
 * actually has to be true is that you own a line *at least* as long as the one
 * you drew. Only stations with a fabricator can recombine, so cutting is
 * one-way and choosing which line to spend is a real decision — which is why
 * this offers the tightest fit rather than silently carving up your longest.
 */
import { EPSILON } from '../geometry/types.js';
/**
 * A line's length below which it stops being a line.
 *
 * From the design doc: offcuts under this convert to linelits rather than
 * staying in cargo as an unusably short piece.
 */
export const MIN_LINE_LENGTH = 1;
/**
 * The best line in stock for a given length, or a statement that there is none.
 *
 * Floating-point slack is not optional here: lengths come out of square roots
 * of grid offsets, so "exactly 5" is a thing you approach, not a thing you hit.
 */
export function fitLine(stock, needed) {
    const slack = Math.max(EPSILON, needed * 1e-9);
    let best = null;
    for (const line of stock) {
        if (line.length + slack < needed)
            continue;
        if (Math.abs(line.length - needed) <= slack) {
            return { kind: 'exact', line, remainder: 0, remainderBecomesLinelits: false };
        }
        if (!best || line.length < best.length)
            best = line;
    }
    if (!best)
        return { kind: 'tooShort', line: null, remainder: 0, remainderBecomesLinelits: false };
    const remainder = best.length - needed;
    return { kind: 'cut', line: best, remainder, remainderBecomesLinelits: remainder < MIN_LINE_LENGTH };
}
/**
 * Spend a line from stock.
 *
 * Returns new arrays rather than mutating, so the editor can price a placement
 * while it is still being dragged without having quietly spent anything.
 */
export function consumeLine(stock, fit, offcutId) {
    if (!fit.line)
        return { stock: [...stock], linelits: 0 };
    const remaining = stock.filter((l) => l.id !== fit.line.id);
    if (fit.kind === 'exact')
        return { stock: remaining, linelits: 0 };
    if (fit.remainderBecomesLinelits)
        return { stock: remaining, linelits: fit.remainder };
    // The offcut keeps its parent's stats — it is the same physical line, shorter.
    return {
        stock: [...remaining, { ...fit.line, id: offcutId, length: fit.remainder }],
        linelits: 0,
    };
}
//# sourceMappingURL=stock.js.map